import { gql } from '@apollo/client/core';
import { Image, Page, StyleSheet, View } from '@react-pdf/renderer';
import React from 'react';
import {
  Box,
  Company,
  Flex,
  Image as Bottlebooks_Image,
  QRCode,
  Registration,
  SemiTitle,
  // Stencil,
  Text,
} from '../../pdf-design-system';
import { ThemeProvider } from '../../pdf-design-system/useTheme';
import UserLocaleProvider from '../../Providers/UserLocaleProvider';
import Gothic400 from './fonts/gothic400';
import Gothic700 from './fonts/gothic700';
import messages from './locale/messages';
// import logo from './images/californiaLogo.jpg';
// import stencil from './images/stencil.jpg';

/**
 * The Layout component is responsible for rendering one or multiple pages.
 * It sets the page size and sets up the wrapping.
 * In this case we have multiple items per page that might wrap, so we add a single page only.
 * It renders the page header and footer, and a list of sections in between.
 * A closing spacer ensures that the footer is on the page bottom,
 * without resorting to absolute positioning.
 * The stencil will be placed correctly if we use `padding` for the page margins.
 */

const bleed = 0.25; //in

export default function ABSTableSigns({
  data,
  showStencil,
  collectionId,
  exhibitorId,
  locale,
  ...rest
}) {
  const sections = afterQuery(data, rest);

  return (
    <ThemeProvider theme={ABSTableSigns.theme}>
      <UserLocaleProvider locale={locale} messages={messages}>
        {sections.map((section, index) => {
          const registration = processRegistration(section);
          return (
            <Page size="A4" key={registration.registrationId} {...rest}>
              {/* ISSUE: Stencil is not rendering full height */}
              {/* {showStencil && <Stencil src={stencil} fixed />} */}
              <Registration.Provider value={registration}>
                <Box>
                  <View style={{ height: 275 }}>
                    <Image
                      src="https://res.cloudinary.com/bottlebooks/image/upload/v1660816852/pdf-objects/ABSWineAgencies/Banner_table_numbers.jpg"
                      style={[
                        {
                          objectFit: 'cover',
                          objectPosition: 'center top',
                        },
                      ]}
                    />
                  </View>
                  <Flex
                    style={{
                      height: '60',
                      textTransform: 'uppercase',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      backgroundColor: '#53BEC9',
                    }}
                  >
                    <Text>{registration?.stand?.name}</Text>
                    {/* <Title style={{ fontSize: 28, fontWeight: 600 }}>
                  As Alive as the Place They’re Grown
                </Title> */}
                  </Flex>
                </Box>
                <Box
                  wrap={false}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: `${2 * bleed}in`,
                    flexGrow: 1,
                  }}
                >
                  <Flex
                    style={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      textAlign: 'center',
                      flexGrow: 1,
                    }}
                  >
                    <SemiTitle style={{ marginBottom: 7 }}>
                      {registration?.tableSignCustomizedExhibitorName?.value ||
                        registration?.profile?.name ||
                        ' '}
                    </SemiTitle>

                    {/* We need different approaches to render the brands based on the number.
                  If there are less than 10, then we can manage by changing font size.
                  If there are more than 10, then we switch to a comma-separated list. */}
                    {registration?.byBrand?.brands?.length >= 0 && (
                      <Flex
                        style={{
                          alignItems: 'center',
                          flexDirection: 'column',
                          textAlign: 'center',
                        }}
                      >
                        {registration?.byBrand?.brands?.length <= 1 ? (
                          registration.byBrand?.brands.map(({ name }) => (
                            <Text
                              key={name}
                              style={[
                                registration.byBrand?.brands.length > 3 &&
                                  registration.byBrand?.brands.length < 7 && {
                                    fontSize: 24,
                                  },
                                registration.byBrand?.brands.length >= 7 && {
                                  fontSize: 18,
                                },
                              ]}
                            >
                              {name}
                            </Text>
                          ))
                        ) : (
                          <Text
                            style={[{ fontSize: 20, paddingHorizontal: '1cm' }]}
                          >
                            {registration?.byBrand?.brands
                              ?.map(({ name }) => name)
                              .join(', ')}
                          </Text>
                        )}
                      </Flex>
                    )}
                  </Flex>
                </Box>

                <Flex
                  style={{
                    width: '100%',
                    paddingBottom: `${0.5 + bleed}in`,
                    justifyContent: 'space-around',
                    alignItems: 'center',
                  }}
                  {...rest}
                >
                  <Flex direction="columnn" style={{ alignItems: 'center' }}>
                    <QRCode
                      url={`${registration.collection?.site?.publicUrl}/e/${registration.companyId}`}
                      errorCorrectionLevel="L"
                      margin={0}
                      style={{
                        width: '1.4in',
                        height: '1.4in',
                        flexShrink: 0,
                      }}
                    />
                    <Text style={{ fontSize: 14, marginTop: 8 }}>
                      Scan to take notes
                    </Text>
                  </Flex>
                  <Company.ProfileLogo
                    companyProfile={{
                      logo: registration.profile.autoSizedLogo,
                    }}
                    height={150}
                    width={200}
                  />
                </Flex>
              </Registration.Provider>
            </Page>
          );
        })}
      </UserLocaleProvider>
    </ThemeProvider>
  );
}

ABSTableSigns.fragment = gql`
  fragment ABSTableSigns on CatalogConfiguration {
    catalogConfigurationId
    collectionId
    collection {
      collectionId
      eventId: collectionId
      site {
        publicUrl
      }
    }
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      sectionType
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
        }
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...ABSTableSigns_Table
    }
  }

  fragment ABSTableSigns_Table on RegistrationCatalogSection {
    registration {
      registrationId
      companyId
      collection {
        collectionId
        eventId: collectionId
        site {
          publicUrl
        }
      }
      profile {
        profileId
        name
        autoSizedLogo: logo {
          url
        }
      }
      stand {
        name
      }
      tableSignCustomizedExhibitorName: customFieldValue(
        key: "tableSignCustomizedExhibitorName"
      ) {
        __typename
        ... on TextFieldValue {
          value
        }
      }
      hideBrandsOnTableSign: customFieldValue(key: "hideBrandsOnTableSign") {
        __typename
        ... on YesNoFieldValue {
          value
        }
      }
      byBrand: registeredProducts {
        brands: group(field: producer___name, sort: { field: fieldValue }) {
          name: fieldValue
        }
      }
    }
  }
`;

function afterQuery(data, rest) {
  if (rest.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === rest.companyId
    );
  }
  if (rest.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === rest.registrationId
    );
  }

  return data.sections;
}

function processRegistration({ registration }) {
  // Filter out brands when there is only 1 brand and it has the same name as the exhibitor
  const filteredBrands =
    registration.byBrand?.brands?.length >= 2
      ? // If there is more than one brand, include them all.
        registration.byBrand?.brands
      : // Otherwise see if we need to filter it out
        registration.byBrand?.brands.filter(
          (brand) => brand.name !== registration.profile.name
        );

  return {
    ...registration,
    byBrand: {
      brands: registration.hideBrandsOnTableSign?.value ? [] : filteredBrands,
    },
  };
}

ABSTableSigns.theme = StyleSheet.create({
  page: {},
  headerNormal: {
    fontFamily: 'Gothic',
    fontWeight: 400,
    fontSize: 36,
  },
  components: {
    Text: {
      base: { ...Gothic400 },
      default: { fontSize: 36 },
    },
    Title: {
      base: {
        ...Gothic700,
        textTransform: 'uppercase',
        color: 'white',
      },
      default: {
        fontSize: 48,
      },
    },
    SemiTitle: {
      base: {
        ...Gothic700,
        textTransform: 'uppercase',
        paddingVertical: 4,
      },
      default: { fontSize: 42 },
    },
  },
});
